import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Button, Dialog } from "@rio-cloud/rio-uikit";
import { fetchBudgetHistory, fetchBudgetUserProfile } from "../../../../api/fetchBudget";
import { getRouteByProfile, getKanbanRouteByProfile } from "../../../budgetsKanban/budgetCard/utils";
import { BudgetHistory } from "../../types";
import { getStatusBadgeClass, getRecipientLabel } from "../../../budgetsKanban/utils/statusHelpers";
import BudgetHistoryModalMobile from "./mobile/BudgetHistoryModalMobile";

interface BudgetHistoryModalProps {
  show: boolean;
  budgetId: string;
  consultant: string;
  onClose: () => void;
  onViewBudget?: (budgetId: string, versionNumber?: number) => void;
  onEditBudget?: (budgetId: string, versionNumber?: number) => void;
  userProfile?: string;
}

const BudgetHistoryModal = ({
  show,
  budgetId,
  consultant,
  onClose,
  onViewBudget,
  onEditBudget,
  userProfile,
}: BudgetHistoryModalProps) => {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<BudgetHistory[]>([]);
  const [currentVersion, setCurrentVersion] = useState<BudgetHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px) and (min-width: 769px)");

  useEffect(() => {
    if (show && budgetId) {
      loadVersions();
    }
  }, [show, budgetId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await fetchBudgetHistory(
        budgetId,
        "versionNumber",
        "desc",
        1,
        50,
        true
      );
      setVersions(response.data);
      if (response.data.length > 0) {
        setCurrentVersion(response.data[0]);
      }
    } catch (error) {
      console.error("[BudgetHistoryModal] Erro ao carregar versões:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBudget = async (version: BudgetHistory) => {
    if (onViewBudget) {
      onViewBudget(budgetId, version.version);
      onClose();
      return;
    }

    if (!budgetId) return;

    try {
      const userProfileData = await fetchBudgetUserProfile();
      const baseRoute = getRouteByProfile(userProfileData);
      const kanbanRoute = getKanbanRouteByProfile(userProfileData);
      
      if (!baseRoute) {
        console.error("[BudgetHistoryModal] Perfil do usuário não identificado");
        return;
      }

      const route = `${baseRoute}/${budgetId}/versions/${version.version}${kanbanRoute ? `?redirectBack=${encodeURIComponent(kanbanRoute)}` : ''}`;
      navigate(route);
      onClose();
    } catch (error) {
      console.error("[BudgetHistoryModal] Erro ao redirecionar:", error);
    }
  };

  return (
    <>
      <Dialog
        show={show}
        title="Histórico de atualizações"
        onClose={onClose}
        bsSize={isMobile ? Dialog.SIZE_SM : isTablet ? Dialog.SIZE_MD : Dialog.SIZE_LG}
        footer={
          <div className={`display-flex ${isMobile ? "justify-content-center" : "justify-content-end"}`}>
            <Button bsStyle="default" onClick={onClose} className={isMobile ? "w-full" : ""}>
              Fechar
            </Button>
          </div>
        }
        body={
          loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinnerInfoBox display-flex justify-content-center align-items-center">
                <div className="spinner"></div>
                <div className="spinnerText">Carregando histórico...</div>
              </div>
            </div>
          ) : isMobile ? (
            <BudgetHistoryModalMobile
              consultant={consultant}
              currentVersion={currentVersion!}
              versions={versions}
              onViewBudget={handleViewBudget}
            />
          ) : (
            <div className="p-4">
              {currentVersion && (
                <div className="mb-6">
                  <h3 className="text-size-16 font-semibold mb-4">
                    Histórico de atualizações
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Consultor da concessionária
                          </th>
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Destinatário
                          </th>
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Orçamento
                          </th>
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Versão
                          </th>
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Data
                          </th>
                          <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            {consultant}
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            {getRecipientLabel(currentVersion.user)}
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14`}>
                            <button
                              onClick={() => handleViewBudget(currentVersion)}
                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                            >
                              <span className="rioglyph rioglyph-file"></span>
                              <span>Visualizar orçamento</span>
                            </button>
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            v {currentVersion.version}.0
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            {currentVersion.versionDate}
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3`}>
                            <span
                              className={`px-3 py-1 rounded-full text-size-12 font-medium ${getStatusBadgeClass(currentVersion.status)}`}
                            >
                              {currentVersion.status}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h3 className="text-size-16 font-semibold mb-4">
                  Histórico de alterações:
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                          Perfil
                        </th>
                        <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                          Ver orçamento
                        </th>
                        <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                          Versão
                        </th>
                        <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                          Data da versão
                        </th>
                        <th className={`${isTablet ? "px-2" : "px-4"} py-3 text-left text-size-12 font-semibold text-gray-700`}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((version, index) => (
                        <tr
                          key={`${version.id}-${version.version}-${index}`}
                          className="border-b border-gray-200"
                        >
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            {version.profile}
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14`}>
                            <button
                              onClick={() => handleViewBudget(version)}
                              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                            >
                              <span className="rioglyph rioglyph-file"></span>
                              <span>Visualizar orçamento</span>
                            </button>
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            v {version.version}.0
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3 text-size-14 text-gray-800`}>
                            {version.versionDate}
                          </td>
                          <td className={`${isTablet ? "px-2" : "px-4"} py-3`}>
                            <span
                              className={`px-3 py-1 rounded-full text-size-12 font-medium ${getStatusBadgeClass(version.status)}`}
                            >
                              {version.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        }
      />
    </>
  );
};

export default BudgetHistoryModal;
